import { useCallback, useEffect, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl';
import { useAuth } from 'react-oidc-context';

import {
  NormatimResponse,
  fetchNormatimLocation
} from '@src/Common/Helpers/fetchNormatimLocation';
import { useStores } from '@src/Common/Hooks/useStores';

import {
  IDumGlobalEventDetailHoverData,
  IMapCoordinate
} from '@src/Domain/DUMEvents/DUMEvents.type';
const onSubmitEvent = (
  isAuthenticated: boolean,
  onAuth: () => void,
  onNotAuth: () => void
) => {
  if (isAuthenticated) {
    onAuth();
  } else if (!isAuthenticated) {
    onNotAuth();
  }
};

const useMaps = () => {
  const { isAuthenticated, signinRedirect } = useAuth();
  const { FormStore } = useStores();
  const [showSignInModal, setShowSignInModal] = useState<boolean>(false);
  const [submitEventModal, setSubmitEventModal] = useState<{
    show: boolean;
    coordinate: [number, number];
    location?: string;
  }>({ show: false, coordinate: [0, 0], location: '' });

  const [showFirstData, setShowFirstData] = useState<boolean>(true);
  const [showModalSubmitEventMobile, setShowModalSubmitEventMobile] =
    useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (submitEventModal.show || showModalSubmitEventMobile) {
        const fetchLocation: NormatimResponse | Error =
          await fetchNormatimLocation({
            lat: submitEventModal.coordinate[0],
            lon: submitEventModal.coordinate[1]
          });
        setSubmitEventModal((s) => ({
          ...s,
          location:
            fetchLocation instanceof Error
              ? 'Unknown Location'
              : fetchLocation.display_name
        }));
      }
    })();
  }, [
    submitEventModal.coordinate,
    submitEventModal.show,
    showModalSubmitEventMobile
  ]);

  const [popupData, setPopupData] = useState<{
    latitude: number;
    longitude: number;
    message: string;
    dumId: string;
    isMythicEvent: boolean;
  }>({
    latitude: 1,
    longitude: -1,
    message: '',
    dumId: '',
    isMythicEvent: false
  });
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [hoverData, setHoverData] = useState<IDumGlobalEventDetailHoverData[]>(
    []
  );
  const [eventDetail, setEventDetail] = useState<IMapCoordinate>();
  const { DUMEventStore } = useStores();
  const [viewState, setViewState] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 2
  });

  const mapRef = useRef<MapRef | null>(null);

  const onLoad = async () => {
    document.querySelector('.mapboxgl-ctrl-bottom-right')?.remove();
    document.querySelector('.mapboxgl-ctrl-bottom-left')?.remove();
    DUMEventStore.mapReady = true;
    if (mapRef.current) {
      const canvas = mapRef.current?.getCanvas();
      canvas.style.height = '100%';
      canvas.style.opacity = '0.5';

      // check when have saved_latlong
      const savedLatlong = localStorage.getItem('saved_latlong') || '';
      if (savedLatlong) {
        setSubmitEventModal({
          show: true,
          coordinate: JSON.parse(savedLatlong)
        });
        localStorage.removeItem('saved_latlong');
      }
    }
  };

  const handleFlyAnimation = useCallback((item: IMapCoordinate) => {
    if (!item) {
      return;
    }
    if (!mapRef.current) {
      return;
    }
    setShowPopup(true);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [Number(item.coordinates[1]), Number(item.coordinates[0])],
        essential: true
      });
      setPopupData((prev) => ({
        ...prev,
        latitude: item.coordinates[0],
        longitude: item.coordinates[1],
        message: item.city,
        dumId: item.id,
        isMythicEvent: item.isMythicEvent
      }));
      setHoverData(item.hoverData);
    }
  }, []);

  useEffect(() => {
    // will not run if url has dum_id attribute
    const query = new URLSearchParams(window.location.search);
    const year = query.get('year');
    const month = query.get('month')?.padStart(2, '0');
    const dum_id = query.get('dum_id');
    if (year && month && dum_id && showFirstData) {
      const selectedData = DUMEventStore.DUMEventDatas.dataMaps.find(
        (dataItem) => dataItem.id === dum_id
      );
      if (selectedData) {
        handleFlyAnimation(selectedData);
        setShowFirstData(false);
        return;
      }
    }
    const firstData = DUMEventStore.DUMEventDatas.dataMaps.at(0);
    if (!firstData) {
      return;
    }
    handleFlyAnimation(firstData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DUMEventStore.DUMEventDatas.dataMaps, handleFlyAnimation]);

  useEffect(() => {
    if (DUMEventStore.DUMEventDatas.status.isLoading) {
      // * hide popup when loading begin
      setShowPopup(false);
    }
  }, [DUMEventStore.DUMEventDatas.status.isLoading]);

  const normalizeCoordinates = (
    latitude: number,
    longitude: number
  ): [number, number] => {
    const lessThanMinus90 =
      latitude < -90
        ? ((latitude + 90) % 180) - 90
        : ((latitude + 90) % 180) + 90;
    const normalizedLatitude =
      latitude >= -90 && latitude <= 90 ? latitude : lessThanMinus90;
    const lessThanMinus180 =
      longitude < -180
        ? ((longitude + 180) % 360) + 180
        : ((longitude + 180) % 360) - 180;
    const normalizedLongitude =
      longitude >= -180 && longitude <= 180 ? longitude : lessThanMinus180;

    return [normalizedLatitude, normalizedLongitude];
  };

  const handleValidationClick = (
    width: number,
    isChangeLocation: boolean,
    latitude: number,
    longitude: number
  ) => {
    if (width < 767) {
      if (isChangeLocation) {
        setSubmitEventModal((prev) => ({
          ...prev,
          coordinate: [latitude, longitude]
        }));
        FormStore.setCoordinate(`${latitude}`, `${longitude}`);
        FormStore.showModal();
      } else {
        setShowModalSubmitEventMobile(true);
        setSubmitEventModal((prev) => ({
          ...prev,
          coordinate: [latitude, longitude]
        }));
        FormStore.setCoordinate(`${latitude}`, `${longitude}`);
      }
    } else {
      if (isChangeLocation) {
        setSubmitEventModal((prev) => ({
          ...prev,
          coordinate: [latitude, longitude]
        }));
        FormStore.setCoordinate(`${latitude}`, `${longitude}`);
        FormStore.showModal();
      } else {
        setSubmitEventModal((prev) => ({
          ...prev,
          show: !prev.show,
          coordinate: [latitude, longitude]
        }));
        FormStore.setCoordinate(`${latitude}`, `${longitude}`);
      }
    }
  };

  const onSubmitEventMobile = () => {
    onSubmitEvent(
      isAuthenticated,
      () => {
        setShowModalSubmitEventMobile(false);
        FormStore.showModal();
      },
      async () => {
        localStorage.setItem(
          'saved_latlong',
          JSON.stringify(submitEventModal.coordinate)
        );
        await signinRedirect();
      }
    );
  };

  return {
    showSignInModal,
    setShowSignInModal,
    submitEventModal,
    setSubmitEventModal,
    showModalSubmitEventMobile,
    setShowModalSubmitEventMobile,
    mapRef,
    onLoad,
    showPopup,
    setShowPopup,
    hoverData,
    setHoverData,
    eventDetail,
    setEventDetail,
    viewState,
    setViewState,
    popupData,
    setPopupData,
    handleFlyAnimation,
    isAuthenticated,
    signinRedirect,
    normalizeCoordinates,
    handleValidationClick,
    onSubmitEventMobile
  };
};

export default useMaps;
